<script lang="ts">
  /**
   * Панель керування плеєра: однакова на всіх пристроях. Сама нічого не знає
   * про <video> — отримує стан і викликає дії, тож її ж показує попередній
   * перегляд в адмінці.
   */
  import { untrack } from 'svelte';
  import Icon from '../Icon.svelte';
  import PlayerProgress from './PlayerProgress.svelte';
  import PlayerMenu from './PlayerMenu.svelte';
  import { formatTime, type PlayerSettings } from '../../lib/player/settings';
  import type { PlayerActions, PlayerCaps, PlayerState } from '../../lib/player/types';

  interface Props {
    state: PlayerState;
    actions: PlayerActions;
    caps: PlayerCaps;
    settings: PlayerSettings;
  }
  let { state: s, actions, caps, settings }: Props = $props();

  const HIDE_AFTER_MS = 3000;
  const DOUBLE_TAP_MS = 300;

  let rootEl = $state<HTMLDivElement | undefined>(undefined);
  let active = $state(true); // була недавня взаємодія
  let menuOpen = $state(false);
  let dragging = $state(false);
  let skipFlash = $state<'back' | 'forward' | null>(null);
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  let lastTapAt = 0;

  // На паузі, з відкритим меню й під час перетягування панель не ховається.
  const visible = $derived(active || s.paused || menuOpen || dragging || s.failed);
  const showQuality = $derived(settings.controls.quality && s.qualities.length > 1);
  const showSpeed = $derived(settings.controls.speed && settings.speeds.length > 1);

  function bump(): void {
    active = true;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => (active = false), HIDE_AFTER_MS);
  }

  // Відтворення пішло (кнопкою, клавішею, після перемикання якості) — відлік
  // до приховування починається заново, а не триває від останнього дотику.
  $effect(() => {
    if (!s.paused) untrack(bump);
  });

  /** Частка ширини плеєра, на яку припав дотик, у координатах самого плеєра. */
  function tapFraction(e: PointerEvent): number {
    if (!rootEl) return 0.5;
    const r = rootEl.getBoundingClientRect();
    return s.rotated ? (e.clientY - r.top) / r.height : (e.clientX - r.left) / r.width;
  }

  function doSkip(dir: 'back' | 'forward'): void {
    actions.skip(dir === 'back' ? -settings.skipSeconds : settings.skipSeconds);
    skipFlash = dir;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => (skipFlash = null), 500);
  }

  function onSurfaceUp(e: PointerEvent): void {
    if (e.pointerType === 'mouse') {
      // Миша: клік — пауза, панель і так видно, поки курсор рухається.
      actions.togglePlay();
      bump();
      return;
    }
    const now = Date.now();
    if (now - lastTapAt < DOUBLE_TAP_MS) {
      lastTapAt = 0;
      const f = tapFraction(e);
      if (f < 0.4) doSkip('back');
      else if (f > 0.6) doSkip('forward');
      bump();
      return;
    }
    lastTapAt = now;
    // Дотик: показати панель, а якщо вже видно — сховати.
    if (visible && !s.paused) {
      active = false;
      clearTimeout(hideTimer);
    } else {
      bump();
    }
  }

  function onMouseMove(e: PointerEvent): void {
    if (e.pointerType === 'mouse') bump();
  }

  function onLeave(e: PointerEvent): void {
    // Для дотику pointerleave приходить на кожне відпускання пальця — це не «курсор пішов».
    if (e.pointerType === 'mouse') active = false;
  }

  function pick(fn: () => void): void {
    fn();
    menuOpen = false;
    bump();
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="controls"
  class:hidden={!visible}
  bind:this={rootEl}
  onpointermove={onMouseMove}
  onpointerleave={onLeave}
>
  <div class="surface" onpointerup={onSurfaceUp} ondblclick={actions.toggleFullscreen}></div>

  {#if skipFlash}
    <div class="flash {skipFlash}">
      <Icon name={skipFlash === 'back' ? 'rotate-ccw' : 'rotate-cw'} size="lg" />
      {settings.skipSeconds} с
    </div>
  {/if}

  {#if s.failed}
    <div class="center">
      <div class="error">
        Не вдалося відтворити відео
        <button class="retry" onclick={actions.retry}>Спробувати ще</button>
      </div>
    </div>
  {:else if s.waiting && !s.paused}
    <div class="center"><div class="spinner"></div></div>
  {:else if s.paused && settings.controls.bigPlay}
    <div class="center">
      <button class="big-play" onclick={actions.togglePlay} aria-label="Відтворити">
        <Icon name="play" size="xl" filled />
      </button>
    </div>
  {/if}

  <div class="bar">
    <PlayerProgress
      currentTime={s.currentTime}
      duration={s.duration}
      buffered={s.buffered}
      rotated={s.rotated}
      onSeek={(sec) => {
        actions.seek(sec);
        bump();
      }}
      onDragChange={(d) => (dragging = d)}
    />
    <div class="row">
      <button onclick={() => pick(actions.togglePlay)} aria-label={s.paused ? 'Відтворити' : 'Пауза'}>
        <Icon name={s.paused ? 'play' : 'pause'} filled />
      </button>
      {#if settings.controls.skip}
        <button class="wide-only" onclick={() => doSkip('back')} aria-label="Назад на {settings.skipSeconds} секунд">
          <Icon name="rotate-ccw" />
        </button>
        <button class="wide-only" onclick={() => doSkip('forward')} aria-label="Вперед на {settings.skipSeconds} секунд">
          <Icon name="rotate-cw" />
        </button>
      {/if}
      {#if settings.controls.currentTime || settings.controls.duration}
        <div class="time">
          {#if settings.controls.currentTime}{formatTime(s.currentTime)}{/if}{#if settings.controls.duration}<span
              class="wide-only">{settings.controls.currentTime ? ' / ' : ''}{formatTime(s.duration)}</span
            >{/if}
        </div>
      {/if}

      <div class="spacer"></div>

      {#if settings.controls.volume}
        <button onclick={() => pick(actions.toggleMute)} aria-label={s.muted ? 'Увімкнути звук' : 'Вимкнути звук'}>
          <Icon name={s.muted || s.volume === 0 ? 'volume-x' : 'volume-2'} />
        </button>
        {#if caps.volume}
          <input
            class="volume"
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={s.muted ? 0 : s.volume}
            oninput={(e) => actions.setVolume(Number(e.currentTarget.value))}
            aria-label="Гучність"
          />
        {/if}
      {/if}
      {#if showQuality || showSpeed}
        <button onclick={() => (menuOpen = true)} aria-label="Якість і швидкість">
          <Icon name="settings-2" />
        </button>
      {/if}
      {#if settings.controls.airplay && caps.airplay && s.airplayAvailable}
        <button class:on={s.airplayActive} onclick={() => pick(actions.airplay)} aria-label="AirPlay">
          <Icon name="airplay" />
        </button>
      {/if}
      {#if settings.controls.pip && caps.pip}
        <button
          class="wide-only"
          class:on={s.pipActive}
          onclick={() => pick(actions.togglePip)}
          aria-label="Картинка в картинці"
        >
          <Icon name="picture-in-picture-2" />
        </button>
      {/if}
      {#if settings.controls.fullscreen}
        <button
          onclick={() => pick(actions.toggleFullscreen)}
          aria-label={s.fullscreen ? 'Згорнути' : 'На весь екран'}
        >
          <Icon name={s.fullscreen ? 'minimize' : 'maximize'} />
        </button>
      {/if}
    </div>
  </div>

  {#if menuOpen}
    <PlayerMenu
      qualities={showQuality ? s.qualities : []}
      quality={s.quality}
      speeds={showSpeed ? settings.speeds : []}
      speed={s.rate}
      onQuality={(h) => pick(() => actions.setQuality(h))}
      onSpeed={(r) => pick(() => actions.setRate(r))}
      onClose={() => (menuOpen = false)}
    />
  {/if}
</div>

<style>
  .controls {
    position: absolute;
    inset: 0;
    container-type: inline-size;
    color: #fff;
    font-size: 13px;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
  .surface {
    position: absolute;
    inset: 0;
  }
  .center {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .center > * {
    pointer-events: auto;
  }
  .big-play {
    width: 64px;
    height: 64px;
    border: none;
    border-radius: 50%;
    background: var(--player-accent);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 4px; /* трикутник оптично зміщений ліворуч */
    cursor: pointer;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  }
  .spinner {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.25);
    border-top-color: #fff;
    animation: spin 0.8s linear infinite;
    pointer-events: none;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 16px;
    text-align: center;
    font-size: 14px;
  }
  .retry {
    padding: 8px 16px;
    border: none;
    border-radius: 999px;
    background: var(--player-accent);
    color: #fff;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  .flash {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 12px 16px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.55);
    font-weight: 600;
    pointer-events: none;
  }
  .flash.back {
    left: 12%;
  }
  .flash.forward {
    right: 12%;
  }

  .bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 18px 10px 4px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
    transition: opacity 0.2s;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .row button {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .row button.on {
    color: var(--player-accent);
  }
  @media (hover: hover) {
    .row button:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }
  .time {
    margin-left: 6px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .spacer {
    flex: 1;
  }
  .volume {
    width: 70px;
    margin: 0 6px 0 2px;
    accent-color: var(--player-accent);
  }

  /* Вбудований плеєр на телефоні вузький: другорядне ховаємо — перемотування
     лишається подвійним дотиком, а в повному екрані місця вистачає на все. */
  @container (max-width: 460px) {
    .wide-only {
      display: none !important;
    }
  }

  .controls.hidden {
    cursor: none;
  }
  .controls.hidden .bar {
    opacity: 0;
    pointer-events: none;
  }
</style>
